import htmlToPdfmake from "html-to-pdfmake"
import {
    NO_DANGEROUS_FAILURE,
    NO_SAFE_FAILURE,
    IEC_61508_2010,
    IEC_61508_1998,
    ISA_TR84_2002,
    ISA_TR84_2022,
    VDI_VDE_2180_2019,
    LOW_DEMAND_MODE,
    HIGH_DEMAND_MODE,
    CONTINUOUS_MODE,
} from "const"
import { SilSafetyFunction } from "server/models"
import { SilCalculationMethod, IMAGE_BASE64_TYPE } from "client/utils"
import { LangType, SilResultSafetyFunctionList, UserSettings } from "client/store"

import { getMaxProbability, getModelSil } from "client/pages/sil/formulas"
import { FormatProbabilityString } from "client/pages/sil/format/probability"

import * as Const from "client/components/pdfmake/template/const"
import { Calculate } from "../reportContent/calculation"
import { Configurate } from "../reportContent/configuration"
import { SubsystemArchitectureParametersPdfExport } from "../reportContent/subsystemArchitectureParameters"

const SafetyFunctionsPage = (
    safetyFunctions: readonly SilSafetyFunction[],
    calculatedSafetyFunctions: SilResultSafetyFunctionList,
    deviceImageBase64: IMAGE_BASE64_TYPE,
    userSettings: UserSettings,
    lang: LangType,
) => {
    let content: any[] = []

    safetyFunctions.forEach((safetyFunction, index) => {
        const resultSafetyFunction = calculatedSafetyFunctions[safetyFunction.id]
        if (resultSafetyFunction) {
            // calculation method can be or dangerous or safe or "no dangerous/safe failure", never both
            let calculationMethod: SilCalculationMethod = null
            if (resultSafetyFunction.calculationMethodDangerous !== NO_DANGEROUS_FAILURE) {
                calculationMethod = resultSafetyFunction.calculationMethodDangerous
            }
            if (resultSafetyFunction.calculationMethodSafe !== NO_SAFE_FAILURE) {
                calculationMethod = resultSafetyFunction.calculationMethodSafe
            }

            let calculationMethodString: string
            if (calculationMethod === IEC_61508_2010) calculationMethodString = lang.simplifiedEquationsIec_61508_2010
            if (calculationMethod === IEC_61508_1998) calculationMethodString = lang.simplifiedEquationsIec_61508_1998
            if (calculationMethod === ISA_TR84_2002) calculationMethodString = lang.simplifiedEquationsIsa_tr84_2002
            if (calculationMethod === ISA_TR84_2022) calculationMethodString = lang.simplifiedEquationsIsa_tr84_2022
            if (calculationMethod === VDI_VDE_2180_2019) calculationMethodString = lang.simplifiedEquationsVdi_vde_2180_2019

            let mode: string
            if (resultSafetyFunction.mode === LOW_DEMAND_MODE) mode = lang.modeLowDemand
            if (resultSafetyFunction.mode === HIGH_DEMAND_MODE) mode = lang.modeHighDemand
            if (resultSafetyFunction.mode === CONTINUOUS_MODE) mode = lang.modeContinuous

            let targetSil: string
            if (resultSafetyFunction.targetSil === null) targetSil = lang.noTargetSet
            if (resultSafetyFunction.targetSil !== null) targetSil = lang[`sil_${resultSafetyFunction.targetSil}`]

            let pdfTargetTitle: string
            if (resultSafetyFunction.mode === LOW_DEMAND_MODE) pdfTargetTitle = lang.targetPfd
            if (
                resultSafetyFunction.mode === HIGH_DEMAND_MODE
                || resultSafetyFunction.mode === CONTINUOUS_MODE
            ) pdfTargetTitle = lang.targetPfh

            // pfd target
            const pdfTarget = resultSafetyFunction.targetPfd !== null
                ? resultSafetyFunction.targetPfd
                : getMaxProbability(resultSafetyFunction.mode, resultSafetyFunction.targetSil)

            const targetStl = resultSafetyFunction.targetStl !== null
                ? lang.stlLevel.replace("{0}", resultSafetyFunction.targetStl.toString())
                : null

            // str target
            const strTarget = resultSafetyFunction.targetStr !== null
                ? resultSafetyFunction.targetStr
                : 0

            // architecture
            const architectureTables: any[] = []
            let titleColumns: any[] = []
            let bodyColumns: any[] = []

            const columnWidth = 100
            const maxColumns = 7

            const subsystemList = resultSafetyFunction.subsystems || []
            subsystemList.forEach((subsystem) => {
                const subsystemBodyRows: any[] = []
                subsystem.blocks.forEach((block) => {
                    const { projectDevice } = block
                    const image = deviceImageBase64[projectDevice.id]
                    const blockSize = 55
                    let marginTop = 0
                    if (image.height < image.width) {
                        const newHeight = (image.height / image.width) * blockSize
                        marginTop = (blockSize - newHeight) / 2
                    }

                    const deviceSil = getModelSil(projectDevice, subsystem.architecturalConstraints, lang, true) as string

                    subsystemBodyRows.push([{
                        stack: [
                            {
                                table: { // inner table
                                    widths: [blockSize],
                                    heights: blockSize,
                                    body: [
                                        [{
                                            image: image.data,
                                            fit: [blockSize, blockSize],
                                            alignment: "center",
                                            margin: [0, marginTop, 0, 0],
                                        }],
                                    ],
                                },
                                margin: [4, 0, 0, 0],
                            },
                            {
                                text: deviceSil,
                                width: blockSize,
                                margin: [0, deviceSil ? 5 : 0, 0, 0],
                                alignment: "center",
                            },
                        ],
                    }])
                })

                titleColumns.push({
                    text: subsystem.name,
                    alignment: "center",
                })
                bodyColumns.push({
                    stack: [
                        {
                            style: Const.TABLE,
                            layout: Const.TABLE_ARCHITECTURE_LAYOUT,
                            table: {
                                widths: [74], // blockSize + left-right padding
                                body: subsystemBodyRows,
                            },
                        },
                        {
                            text: subsystem.architecture,
                            margin: [0, 5, 0, 0],
                            alignment: "center",
                        },
                    ],
                })

                if (titleColumns.length === maxColumns) {
                    architectureTables.push({
                        layout: Const.TABLE_ARCHITECTURE_PAGE_LAYOUT,
                        table: { // page table
                            widths: Array(titleColumns.length).fill(columnWidth),
                            body: [
                                titleColumns,
                                bodyColumns,
                            ],
                        },
                    })
                    titleColumns = []
                    bodyColumns = []
                }
            })
            if (titleColumns.length) {
                architectureTables.push({
                    layout: Const.TABLE_ARCHITECTURE_PAGE_LAYOUT,
                    table: { // page table
                        widths: Array(titleColumns.length).fill(columnWidth),
                        body: [
                            titleColumns,
                            bodyColumns,
                        ],
                    },
                })
            }

            const architectureContent: any = {
                stack: [],
            }
            architectureTables.forEach((table, pageIndex) => {
                if (pageIndex === 0) {
                    architectureContent.stack.push(table)
                } else {
                    architectureContent.stack.push({
                        pageBreak: "before",
                        ...table,
                    })
                }
            })


            // calculation results
            const calculationResults = Calculate(resultSafetyFunction, userSettings, lang)
            const calculationTableWidths: string[] = Array(calculationResults.columns.length - 1).fill("*")
            calculationTableWidths.unshift("auto")

            const calculationTableRows = calculationResults.rows.slice() as any[][]
            calculationTableRows[calculationTableRows.length - 1][0] = {
                bold: true,
                text: calculationTableRows[calculationTableRows.length - 1][0],
            }

            const calculationHeaderRow: any[] = []
            calculationResults.columns.forEach((column) => {
                calculationHeaderRow.push({
                    text: column,
                    bold: true,
                })
            })
            calculationTableRows.unshift(calculationHeaderRow)

            const outputRows: any[] = []
            calculationResults.output.forEach((row) => {
                outputRows.push({
                    text: [
                        { bold: true, text: row.title },
                        row.value,
                    ],
                })
            })

            // configuration
            const configurationResults = Configurate(resultSafetyFunction, userSettings, lang)
            const { failureRateYesDevices, failureRateNoDevices } = configurationResults

            // failure rate yes devices
            const failureRateYesWidths = [
                10, 60, // device with nested offset
                50, // model
                50, // brand
                30, // type
                50, // sd
                50, // su
                50, // dd
                50, // du
            ]

            const failureRateYesHeaderRow: any[] = []
            failureRateYesDevices.columns.forEach((column, columnIndex) => {
                if (columnIndex === 0) {
                    failureRateYesHeaderRow.push(
                        {
                            text: column,
                            colSpan: 2,
                            bold: true,
                        },
                        {},
                    )
                } else {
                    failureRateYesHeaderRow.push({
                        text: column,
                        bold: true,
                    })
                }
            })

            const failureRateYesRows: any[][] = [failureRateYesHeaderRow]

            failureRateYesDevices.rows.forEach((row) => {
                let rowColumns: any[]
                if (!row.nested) {
                    rowColumns = [
                        {
                            text: row.columns[0],
                            colSpan: 2,
                        },
                        {},
                    ]
                } else {
                    rowColumns = [
                        "",
                        row.columns[0],
                    ]
                }
                rowColumns = rowColumns.concat(row.columns.slice(1))
                failureRateYesRows.push(rowColumns)
            })

            // failure rate no devices
            const failureRateNoWidths = [
                60, // device
                50, // model
                50, // brand
                30, // type
                45, // sff
                45, // dc
                45, // pfd
                45, // pfh
                30, // sil
            ]

            const failureRateNoHeaderRow: any[] = []
            failureRateNoDevices.columns.forEach((column) => {
                failureRateNoHeaderRow.push({
                    text: column,
                    bold: true,
                })
            })

            const failureRateNoRows = failureRateNoDevices.rows.slice() as any[][]
            failureRateNoRows.unshift(failureRateNoHeaderRow)

            // subsystem parameters
            const subsystemParameters = SubsystemArchitectureParametersPdfExport(resultSafetyFunction, userSettings, lang)

            const tocItemIndex = index + 1
            content = content.concat([
                {
                    pageBreak: "before",
                    style: Const.HEADER,
                    text: `2.${tocItemIndex}  ${safetyFunction.name}`,
                    ...Const.TOC_ITEM_BASE,
                    tocMargin: [0, index === 0 ? 0 : 8, 0, 0],
                },
                {
                    style: Const.SUB_HEADER,
                    text: `2.${tocItemIndex}.1   ${lang.safetyFunctionDescription}`,
                    ...Const.TOC_ITEM_BASE,
                },
                {
                    style: Const.PARAGRAPH,
                    stack: htmlToPdfmake(safetyFunction.description),
                },

                // safety function parameters
                {
                    style: Const.SUB_HEADER,
                    text: `2.${tocItemIndex}.2   ${lang.safetyFunctionParameters}`,
                    ...Const.TOC_ITEM_BASE,
                },
                {
                    style: Const.LIST,
                    ul: [
                        `${lang.calculationMethod}: ${calculationMethodString}`,
                        `${lang.mode}: ${mode}`,
                        resultSafetyFunction.calculationMethodDangerous !== NO_DANGEROUS_FAILURE
                            ? `${lang.targetSilLevel}: ${targetSil}`
                            : null,
                        resultSafetyFunction.calculationMethodDangerous !== NO_DANGEROUS_FAILURE
                            ? `${pdfTargetTitle}: ${FormatProbabilityString(pdfTarget, userSettings, lang)}`
                            : null,
                        resultSafetyFunction.calculationMethodSafe !== NO_SAFE_FAILURE && resultSafetyFunction.targetStl !== null
                            ? `${lang.targetStlLevel}: ${targetStl}`
                            : null,
                        resultSafetyFunction.calculationMethodSafe !== NO_SAFE_FAILURE && resultSafetyFunction.targetStl !== null
                            ? `${lang.targetStr}: ${FormatProbabilityString(strTarget, userSettings, lang)}`
                            : null,
                    ],
                },

                // architecture
                {
                    pageBreak: "before",
                    pageOrientation: "landscape",
                    style: Const.SUB_HEADER,
                    text: `2.${tocItemIndex}.3   ${lang.architecture}`,
                    ...Const.TOC_ITEM_BASE,
                },
                architectureContent,

                // calculation results
                {
                    pageBreak: "before",
                    pageOrientation: "portrait",
                    style: Const.SUB_HEADER,
                    text: `2.${tocItemIndex}.4   ${lang.results}`,
                    ...Const.TOC_ITEM_BASE,
                },
                {
                    style: Const.TABLE,
                    layout: Const.TABLE_LAYOUT,
                    table: {
                        headerRows: 1,
                        widths: calculationTableWidths,
                        body: calculationTableRows,
                    },
                },
                {
                    text: calculationResults.annotation,
                    fontSize: 11,
                    alignment: "center",
                    margin: [0, 5, 0, 15],
                },
                {
                    style: Const.PARAGRAPH,
                    fontSize: 11,
                    stack: outputRows,
                },
                calculationResults.targetMetPfd && {
                    style: Const.PARAGRAPH,
                    fontSize: 11,
                    bold: true,
                    text: calculationResults.targetMetPfd,
                },
                calculationResults.targetSilAchieved && {
                    style: Const.PARAGRAPH,
                    fontSize: 11,
                    bold: true,
                    text: calculationResults.targetSilAchieved,
                },
                calculationResults.targetMetStr && {
                    style: Const.PARAGRAPH,
                    fontSize: 11,
                    bold: true,
                    text: calculationResults.targetMetStr,
                },
                calculationResults.targetStlAchieved && {
                    style: Const.PARAGRAPH,
                    fontSize: 11,
                    bold: true,
                    text: calculationResults.targetStlAchieved,
                },
                {
                    stack: [],
                    margin: [0, 0, 0, 10],
                },

                // configuration
                {
                    style: Const.SUB_HEADER,
                    text: `2.${tocItemIndex}.5   ${lang.configuration}`,
                    ...Const.TOC_ITEM_BASE,
                },

                // failure rate yes devices
                failureRateYesDevices.rows.length
                    ? {
                        style: Const.TABLE,
                        layout: Const.TABLE_LAYOUT,
                        table: {
                            headerRows: 1,
                            widths: failureRateYesWidths,
                            body: failureRateYesRows,
                        },
                    } : {},
                failureRateYesDevices.rows.length
                    ? {
                        text: failureRateYesDevices.annotation,
                        fontSize: 11,
                        alignment: "center",
                        margin: [0, 5, 0, 15],
                    } : {},

                // failure rate no devices
                failureRateNoDevices.rows.length
                    ? {
                        style: Const.TABLE,
                        layout: Const.TABLE_LAYOUT,
                        table: {
                            headerRows: 1,
                            widths: failureRateNoWidths,
                            body: failureRateNoRows,
                        },
                    } : {},
                failureRateNoDevices.rows.length
                    ? {
                        text: failureRateNoDevices.annotation,
                        fontSize: 11,
                        alignment: "center",
                        margin: [0, 5, 0, 15],
                    } : {},

                // subsystem parameters
                {
                    style: Const.SUB_HEADER,
                    text: `2.${tocItemIndex}.6   ${lang.subsystemParameters}`,
                    ...Const.TOC_ITEM_BASE,
                },
                {
                    fontSize: 11,
                    stack: subsystemParameters,
                },
            ])
        }
    })

    return content
}
export default SafetyFunctionsPage
